import {
  Box,
  FormControl,
  FormErrorMessage,
  HStack,
  Input,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { DataTable } from "../../../components/data-table";
import { createColumnHelper } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";

import {
  OrganizationType,
  UserAppRole,
  UserOrganizationRole,
} from "../../../API";
import AddButton from "../../create-organization/add-button";
import { v4 as guid } from "uuid";
import RemoveButton from "../../create-organization/remove-button";
import { TeamValue, TeamValues } from "./team";
import {
  stringIsDisabled,
  stringMapUserAppRole,
  stringMapUserOrganizationRole,
} from "../../../strings";
import { MultiSelect } from "../../../components/select";
import { Bookkeeping, OrganizationFull, User } from "../../../models";
import Tooltip from "../../../components/tooltip";
import { Field, FieldProps, getIn } from "formik";
import {
  emailAlreadyExists,
  selectUserOrganizationRoles,
} from "../../../utils";
import { Select, Option as SelectOption } from "../../../components/select";
import { useApprovalEnabled } from "../../../hooks/approvals/useApprovalEnabled";

const columnHelper = createColumnHelper<TeamValue>();

type TeamTableProps = {
  typeNamePlural: string;
  selectedOrganization: OrganizationFull | undefined;
  bookkeepings: Bookkeeping[] | undefined;
  users: User[] | undefined;
  team: TeamValues;
  remove: <TeamValue>(index: number) => TeamValue | undefined;
  replace: <TeamValue>(index: number, value: TeamValue) => void;
  push: <X = any>(obj: X) => void;
};

const TeamTable = ({
  typeNamePlural,
  selectedOrganization,
  bookkeepings,
  users,
  team,
  remove,
  replace,
  push,
}: TeamTableProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number>();
  const [emails, setEmails] = useState<string[]>([]);
  const toast = useToast();
  const isApprovalEnabled = useApprovalEnabled(selectedOrganization?.id);

  useEffect(() => {
    const emailsNew = team.members?.map((m) => m.email) ?? [];
    // Only update if emails have changed
    if (
      emails.length !== emailsNew.length ||
      !emails.every((email, index) => email === emailsNew[index])
    ) {
      setEmails(emails);
    }
  }, [emails, team.members]);

  const columns = useMemo(() => {
    // Resolve the org default approver's display name for employee placeholders (D-23).
    const defaultApproverUser = selectedOrganization?.defaultEmployeeApproverUserId
      ? users?.find(
          (u) => u.id === selectedOrganization.defaultEmployeeApproverUserId
        )
      : undefined;
    const defaultApproverName = defaultApproverUser
      ? `${defaultApproverUser.firstName} ${defaultApproverUser.lastName}`
      : undefined;

    // Approver options: admin/head users in this org, sorted by lastName (D-22).
    // Truth source is the current Formik form state (team.members), so newly-assigned
    // roles immediately appear as selectable approvers.
    const approverOptions: SelectOption[] = (users ?? [])
      .filter((u) => !u.deletedAt)
      .filter((u) => {
        const member = team.members?.find((m) => m.id === u.id);
        return (
          member?.organizationRole === UserOrganizationRole.admin ||
          member?.organizationRole === UserOrganizationRole.head
        );
      })
      .sort((a, b) =>
        (a.lastName ?? "").localeCompare(b.lastName ?? "", "de-CH")
      )
      .map((u) => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }));

    return [
      columnHelper.accessor("lastName", {
        cell: (info) => {
          const isDisabled =
            !!info.row.original.deletedAt ||
            info.row.original.organizationRole ===
              UserOrganizationRole.accountant ||
            (selectedOrganization?.type === OrganizationType.company &&
              info.row.original.appRole === UserAppRole.accountant);
          const fieldName = `members.${info.row.index}.lastName`;
          return (
            <Field name={fieldName}>
              {({ field, form }: FieldProps<number, TeamValues>) => (
                <FormControl
                  isInvalid={
                    (getIn(form.errors, fieldName) &&
                      getIn(form.touched, fieldName)) as boolean
                  }
                >
                  <Tooltip label={isDisabled ? stringIsDisabled : undefined}>
                    <Input
                      defaultValue={info.getValue()}
                      onBlur={(event) => {
                        replace(info.row.index, {
                          ...info.row.original,
                          lastName: event.target.value,
                        });
                      }}
                      isDisabled={isDisabled}
                    />
                  </Tooltip>
                  <FormErrorMessage>
                    {getIn(form.errors, fieldName)}
                  </FormErrorMessage>
                </FormControl>
              )}
            </Field>
          );
        },
        header: "Nachname",
      }),
      columnHelper.accessor("firstName", {
        cell: (info) => {
          const isDisabled =
            !!info.row.original.deletedAt ||
            info.row.original.organizationRole ===
              UserOrganizationRole.accountant ||
            (selectedOrganization?.type === OrganizationType.company &&
              info.row.original.appRole === UserAppRole.accountant);
          const fieldName = `members.${info.row.index}.firstName`;
          return (
            <Field name={fieldName}>
              {({ field, form }: FieldProps<number, TeamValues>) => (
                <FormControl
                  isInvalid={
                    (getIn(form.errors, fieldName) &&
                      getIn(form.touched, fieldName)) as boolean
                  }
                >
                  <Tooltip label={isDisabled ? stringIsDisabled : undefined}>
                    <Input
                      defaultValue={info.getValue()}
                      onBlur={(event) => {
                        replace(info.row.index, {
                          ...info.row.original,
                          firstName: event.target.value,
                        });
                      }}
                      isDisabled={isDisabled}
                    />
                  </Tooltip>
                  <FormErrorMessage>
                    {getIn(form.errors, fieldName)}
                  </FormErrorMessage>
                </FormControl>
              )}
            </Field>
          );
        },
        header: "Vorname",
      }),
      columnHelper.accessor("email", {
        cell: (info) => {
          // Disable if user already exists or is an accountant or organization admin
          const isDisabled =
            !!info.row.original.deletedAt ||
            info.row.original.organizationRole ===
              UserOrganizationRole.accountant ||
            (selectedOrganization?.type === OrganizationType.company &&
              info.row.original.appRole === UserAppRole.accountant) ||
            info.row.original.appRole === UserAppRole.organizationAdmin ||
            users?.find((u) => u.id === info.row.original.id) !== undefined;
          const fieldName = `members.${info.row.index}.email`;
          const hasDuplicates =
            emails &&
            emails.filter((email) => email === info.getValue()).length > 1;
          return (
            <Field name={fieldName}>
              {({ field, form }: FieldProps<number, TeamValues>) => (
                <FormControl
                  isInvalid={
                    hasDuplicates ||
                    ((getIn(form.errors, fieldName) &&
                      getIn(form.touched, fieldName)) as boolean)
                  }
                >
                  <Tooltip label={isDisabled ? stringIsDisabled : undefined}>
                    <Input
                      defaultValue={info.getValue()}
                      onBlur={(event) => {
                        replace(info.row.index, {
                          ...info.row.original,
                          email: event.target.value,
                        });
                      }}
                      isDisabled={isDisabled}
                    />
                  </Tooltip>
                  <FormErrorMessage>
                    {hasDuplicates
                      ? emailAlreadyExists
                      : getIn(form.errors, fieldName)}
                  </FormErrorMessage>
                </FormControl>
              )}
            </Field>
          );
        },
        header: "E-Mail",
      }),
      columnHelper.accessor("organizationRole", {
        cell: (info) => {
          const isDisabled =
            !!info.row.original.deletedAt ||
            info.row.original.organizationRole ===
              UserOrganizationRole.accountant ||
            info.row.original.appRole === UserAppRole.organizationAdmin;
          const optionsUserOrganizationRole: SelectOption[] = (
            isDisabled
              ? Object.values(UserOrganizationRole)
              : selectUserOrganizationRoles
          ).map((role) => ({
            value: role,
            label:
              info.row.original.appRole === UserAppRole.organizationAdmin &&
              role === UserOrganizationRole.admin
                ? stringMapUserAppRole.get(UserAppRole.organizationAdmin) ?? ""
                : stringMapUserOrganizationRole.get(role) ?? "",
          }));
          return (
            <Tooltip label={isDisabled ? stringIsDisabled : undefined}>
              <Select
                value={optionsUserOrganizationRole.find(
                  (option) => option.value === info.getValue()
                )}
                options={optionsUserOrganizationRole}
                onChange={(option) => {
                  replace(info.row.index, {
                    ...info.row.original,
                    organizationRole: option?.value as UserOrganizationRole,
                  });
                }}
                isDisabled={isDisabled}
              />
            </Tooltip>
          );
        },
        header: "Rolle",
      }),
      // Freigeber column only renders when approval workflow is enabled (D-13).
      // When toggled off via Allgemein, the column disappears app-wide.
      ...(isApprovalEnabled
        ? [columnHelper.accessor("approverUserId", {
        cell: (info) => {
          const row = info.row.original;
          const isEmployee =
            row.organizationRole === UserOrganizationRole.employee;
          const isDisabled =
            !!row.deletedAt ||
            row.organizationRole === UserOrganizationRole.accountant;
          const currentValue = info.getValue() ?? null;
          const selectedOption = currentValue
            ? approverOptions.find((o) => o.value === currentValue) ?? null
            : null;

          // Role-dependent placeholder per D-23.
          let placeholder: string;
          if (isEmployee) {
            if (defaultApproverName) {
              placeholder = `Standard: ${defaultApproverName}`;
            } else {
              placeholder = "Kein Standardfreigeber gesetzt";
            }
          } else {
            placeholder = "Kein Freigeber";
          }

          const ariaLabel =
            `Freigeber für ${row.firstName ?? ""} ${row.lastName ?? ""}`.trim();

          // D-23 / D-32 / IN-04 resolution: both roles use gray.500 for WCAG AA
          // contrast (4.5:1 on white; gray.400's 3.7:1 fails normal-text threshold).
          // Semantic distinction between inherited vs empty is carried by the text
          // prefix itself (`Standard: {name}` vs `Kein Freigeber`), satisfying
          // web/CLAUDE.md principle 5 (never rely solely on color).
          const placeholderColor = "gray.500";

          return (
            <Tooltip label={isDisabled ? stringIsDisabled : undefined}>
              <Box minW="180px">
                <Select
                  value={selectedOption}
                  options={approverOptions}
                  placeholder={placeholder}
                  aria-label={ariaLabel}
                  isClearable
                  isDisabled={isDisabled}
                  chakraStyles={{
                    placeholder: (base) => ({
                      ...base,
                      color: placeholderColor,
                    }),
                  }}
                  onChange={(option) => {
                    const next = (option as SelectOption | null)?.value ?? null;
                    replace(info.row.index, {
                      ...row,
                      approverUserId: next,
                    });
                  }}
                />
              </Box>
            </Tooltip>
          );
        },
        header: () => (
          <Tooltip
            label='Standardfreigeber aus Allgemein gilt nur für Mitarbeiter. Admin und Head können optional einen Freigeber für eigene Ausgaben setzen.'
            placement="bottom-start"
          >
            <Box as="span">Freigeber</Box>
          </Tooltip>
        ),
      })]
        : []),
      columnHelper.accessor("bookkeepings", {
        cell: (info) => {
          const disabled =
            !!info.row.original.deletedAt ||
            info.row.original.appRole === UserAppRole.organizationAdmin;
          return (
            <Tooltip label={disabled ? stringIsDisabled : undefined}>
              <Box>
                <MultiSelect
                  options={bookkeepings?.map((b) => ({
                    label: b.name,
                    value: b.id,
                  }))}
                  value={info
                    .getValue()
                    .map((b) => ({ label: b.name, value: b.id }))}
                  onChange={(value) => {
                    if (
                      info.row.original.appRole !==
                      UserAppRole.organizationAdmin
                    ) {
                      replace(info.row.index, {
                        ...info.row.original,
                        bookkeepings: value.map((v) => ({
                          id: v.value,
                          name:
                            bookkeepings?.find((b) => b.id === v.value)?.name ??
                            "",
                        })),
                      });
                    }
                  }}
                  isDisabled={disabled}
                />
              </Box>
            </Tooltip>
          );
        },
        header: "Buchhaltungen",
      }),
    ];
  }, [
    bookkeepings,
    emails,
    isApprovalEnabled,
    replace,
    selectedOrganization?.defaultEmployeeApproverUserId,
    selectedOrganization?.type,
    team.members,
    toast,
    users,
  ]);

  return (
    <VStack alignItems="start" width="full" spacing="16px">
      <Box
        width="full"
        minHeight="418px"
        maxHeight="51vh"
        overflowY="auto"
        borderBottomWidth="1px"
        borderBottomColor="gray.100"
      >
        <DataTable
          typeNamePlural={typeNamePlural}
          columns={columns}
          data={team.members}
          state={team.members ? "success" : "loading"}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
        />
      </Box>
      <HStack>
        <AddButton<TeamValue>
          push={push}
          data={{
            id: guid(),
            lastName: "",
            firstName: "",
            email: "",
            appRole:
              selectedOrganization?.type === OrganizationType.accountant
                ? UserAppRole.accountant
                : UserAppRole.user,
            organizationRole: UserOrganizationRole.employee,
            approverUserId: null,
            bookkeepings: [],
          }}
          isDisabled={
            !!team?.members?.find(
              (m) =>
                m.appRole !== UserAppRole.organizationAdmin &&
                !m.deletedAt &&
                (m.lastName?.length === 0 ||
                  m.firstName?.length === 0 ||
                  m.email?.length === 0)
            )
          }
        />
        <RemoveButton
          remove={remove}
          index={selectedIndex}
          onComplete={() =>
            setSelectedIndex(
              selectedIndex && selectedIndex > 0 ? selectedIndex - 1 : 0
            )
          }
          isDisabled={selectedIndex === undefined}
        />
      </HStack>
    </VStack>
  );
};

export default TeamTable;
